'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { ViduSLogo } from '@/app/components/icons/ViduSLogo';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { buildEditingModeHref, EDITING_MODE_ROUTES, resolveEditingModeFromSearchParams, SITE } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';

type NavigationGroup = 'interaction' | 'editing' | 'mobile-menu';

const API_URL = SITE === 'china' ? 'https://platform.vidu.cn/docs/vidu-s1' : 'https://platform.vidu.com/docs/vidu-s1';
const DOCUMENTATION_URL = 'https://shengshu.feishu.cn/wiki/PSlDwao9GiEGOKkWS2FcWRtOnwe';
const VIDY_APP_URL = 'https://www.vidy.chat?channel=vidu';
const VIDY_ENTRY_IMAGE_SRC = 'https://image01.cf.vidu.studio/vidu/media-asset/vidy-560b0095.webp';

const VidyHeaderEntry = ({ className }: { className?: string }) => {
  const t = useTranslations('viduStreamNavigation');

  return (
    <a
      className={cn('inline-flex shrink-0 transition-opacity hover:opacity-80', className)}
      href={VIDY_APP_URL}
      onClick={() => reportEvent('element_click', { element_name: 'vidu-s-header-vidy', page: 'vidu-s' })}
      rel="noopener noreferrer"
      target="_blank"
    >
      <img alt={t('vidyEntry')} className="h-full w-auto" src={VIDY_ENTRY_IMAGE_SRC} />
    </a>
  );
};

export const ViduStreamHeader = () => {
  const t = useTranslations('viduStreamNavigation');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const editingMode = resolveEditingModeFromSearchParams(searchParams ?? new URLSearchParams());
  const [openGroup, setOpenGroup] = useState<NavigationGroup | null>(null);
  const isHome = pathname === '/vidu-stream';

  // 落地页首屏 header 透明，下滑再渐显背景+模糊：0=全透明，1=实底（0.86 白 +
  // 24px 模糊）。非落地页恒为 1（保持原来的实底）。菜单展开时强制实底另算。
  const [scrollT, setScrollT] = useState(isHome ? 0 : 1);
  useEffect(() => {
    if (!isHome) {
      setScrollT(1);
      return;
    }
    const FADE_DISTANCE = 240;
    const onScroll = () => setScrollT(Math.min(1, window.scrollY / FADE_DISTANCE));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  // 透明态下驱动背景色与模糊；菜单展开时不接管（回退到 className 的实底）。
  const homeFadeStyle: React.CSSProperties | undefined =
    isHome && !openGroup
      ? {
          backgroundColor: `rgb(255 255 255 / ${0.86 * scrollT})`,
          backdropFilter: `blur(${24 * scrollT}px)`,
          WebkitBackdropFilter: `blur(${24 * scrollT}px)`,
        }
      : undefined;

  const groups = [
    {
      id: 'interaction' as const,
      label: t('interaction.label'),
      items: [
        { id: 'onlineAvatar', label: t('interaction.onlineAvatar'), href: '/vidu-stream/avatar' },
        { id: 'offlineAvatar', label: t('interaction.offlineAvatar'), href: '/vidu-stream/avatar/offline' },
        { id: 'interactionApi', label: t('apiPlatform'), href: API_URL, external: true },
        { id: 'interactionDocs', label: t('documentation'), href: DOCUMENTATION_URL, external: true },
      ],
    },
    {
      id: 'editing' as const,
      label: t('editing.label'),
      items: [
        ...EDITING_MODE_ROUTES.map((entry) => ({
          id: entry.navigationKey,
          label: t(`editing.${entry.navigationKey}`),
          href: buildEditingModeHref(entry.id),
          modeId: entry.id,
          external: false,
        })),
        { id: 'editingApi', label: t('apiPlatform'), href: API_URL, external: true },
        { id: 'editingDocs', label: t('documentation'), href: DOCUMENTATION_URL, external: true },
      ],
    },
  ];
  const openItemCount = groups.find((group) => group.id === openGroup)?.items.length ?? 0;

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50 hidden h-[72px] overflow-hidden bg-white/86 text-[#121826] backdrop-blur-[24px] transition-[height,background-color] duration-200 sm:block"
        style={homeFadeStyle}
      >
        <div className="mx-auto flex h-16 w-full max-w-[1920px] items-center justify-between px-6">
          <ClientLink className="inline-flex" href="/vidu-stream" aria-label={t('product')}>
            <ViduSLogo aria-hidden="true" className="h-8 w-auto" />
          </ClientLink>
          <div className="min-w-0 flex-1" />
          {SITE === 'china' ? null : <VidyHeaderEntry className="h-10" />}
          {/* H5 右侧菜单暂时隐藏，后续恢复时重新挂载菜单按钮与 MobileViduStreamMenu。 */}
          {/*
          <button
            type="button"
            aria-label="Open menu"
            className="flex size-9 items-center justify-center rounded-full bg-[#121826]/10 text-[#121826]"
            onClick={() => setOpenGroup('mobile-menu')}
          >
            <BaseLineList className="size-5" />
          </button>
          */}
        </div>
      </header>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 flex h-[72px] overflow-visible bg-white/86 text-[#121826] backdrop-blur-[24px] transition-[height,background-color] duration-200 sm:hidden',
          openGroup && 'bg-white/95',
        )}
        style={openGroup ? { height: `${72 + openItemCount * 48 + 16}px` } : homeFadeStyle}
      >
        <div className="mx-auto flex h-[72px] w-full max-w-[1920px] items-center justify-between px-[4.5%] sm:px-6">
          <ClientLink
            href="/vidu-stream"
            aria-label={t('product')}
            className="inline-flex w-fit text-[#121826] transition-opacity hover:opacity-80"
          >
            <ViduSLogo aria-hidden="true" className="h-10 w-auto" />
          </ClientLink>

          {SITE === 'china' ? (
            <div className="group relative">
              <button
                className="inline-flex h-9 items-center rounded-full border border-[#121826]/20 px-4 font-medium text-[#121826]/80 text-[14px] transition-colors hover:border-[#121826]/40 hover:text-[#121826]"
                onClick={() =>
                  reportEvent('element_click', { element_name: 'vidu-s-header-mini-program', page: 'vidu-s' })
                }
                type="button"
              >
                {t('miniProgram')}
              </button>
              <span
                aria-label={t('miniProgramCode')}
                className="pointer-events-none absolute top-[calc(100%+12px)] right-0 block size-[148px] translate-y-1 rounded-8 border border-black/8 bg-white p-2 opacity-0 shadow-[0_16px_48px_rgba(18,24,38,0.16)] transition-[opacity,transform] group-hover:translate-y-0 group-hover:opacity-100"
                role="img"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  className="size-full object-contain"
                  src="/wechat_minicode_transparent.png"
                />
              </span>
            </div>
          ) : (
            <VidyHeaderEntry className="h-[46px]" />
          )}
        </div>
      </header>
      {/** Mobile menu is rendered outside the clipped header so it can cover the full viewport. */}
      <MobileViduStreamMenu
        open={openGroup === 'mobile-menu'}
        groups={groups}
        editingMode={editingMode}
        pathname={pathname}
        onClose={() => setOpenGroup(null)}
      />
    </>
  );
};

type MobileMenuGroup = {
  id: NavigationGroup;
  label: string;
  items: Array<{ id: string; label: string; href: string; external?: boolean; modeId?: string }>;
};

const MobileViduStreamMenu = ({
  editingMode,
  groups,
  onClose,
  open,
  pathname,
}: {
  editingMode: string;
  groups: MobileMenuGroup[];
  onClose: () => void;
  open: boolean;
  pathname: string;
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] h-dvh w-screen overflow-y-auto bg-[#f8fafd] px-4 text-[#121826] text-[14px] sm:block">
      <div className="flex h-20 shrink-0 items-center justify-between md:h-16">
        <ClientLink href="/vidu-stream" aria-label="Vidu S" onClick={onClose}>
          <ViduSLogo aria-hidden="true" className="h-5 w-auto" />
        </ClientLink>
        <button
          aria-label="Close menu"
          className="flex size-9 items-center justify-center rounded-full bg-[#121826]/12"
          onClick={onClose}
          type="button"
        >
          <BaseLineClose className="size-5" />
        </button>
      </div>
      {groups.map((group) => (
        <section className="mb-5" key={group.id}>
          <div className="mb-2 px-4 font-medium text-[#121826]/48 text-sm">{group.label}</div>
          <div className="space-y-1">
            {group.items.map((item) => {
              const active =
                !item.external &&
                (item.modeId
                  ? pathname.startsWith('/vidu-stream/editing') && item.modeId === editingMode
                  : pathname === item.href || pathname.startsWith(`${item.href}/`));
              const className = cn(
                'flex min-h-11 items-center rounded-10 px-4 font-normal text-[#121826]/80 text-[14px] transition-colors hover:bg-[#121826]/8 hover:text-[#121826]',
                active && 'bg-[#121826]/12 font-medium text-[#121826]',
              );
              return item.external ? (
                <a
                  className={className}
                  href={item.href}
                  key={item.id}
                  onClick={onClose}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {item.label}
                </a>
              ) : (
                <ClientLink
                  aria-current={active ? 'page' : undefined}
                  className={className}
                  href={item.href}
                  key={item.id}
                  onClick={onClose}
                >
                  {item.label}
                </ClientLink>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};
